import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { REFERRAL_CODE_KEY } from '../../../../constants/referral.const';
import { ayeApi } from '../../../../core';
import { GeneratePayUrlResponse } from '../../../../core/api/http-methods/generate-pay-url';
import type {
  PaymentMethodBrand,
  PaymentMethodStep
} from '../../../../types/registration-payment.types';
import {
  GATEWAY_REQUIRES_IMPORTANT_NOTES,
  PAYMENT_METHOD_CONFIG
} from '../constants/payment-method.const';

const IS_DRY_RUN_ENABLED =
  process.env.REACT_APP_FEATURE_REGISTER_DRY_RUN === 'true' ||
  (!process.env.REACT_APP_FEATURE_REGISTER_DRY_RUN &&
    process.env.NODE_ENV === 'development');

const IS_EWALLET_ENABLED =
  process.env.REACT_APP_FEATURE_EWALLET_ENABLED === 'true';

const REQUIRED_FIELDS_ERROR_PREFIX =
  'Please provide the following required fields:';
const VERIFY_BOTH_ERROR =
  'Please verify both phone number and email address with OTP before continuing.';
const VERIFY_PHONE_ERROR =
  'Please verify your phone number with OTP before continuing.';
const VERIFY_EMAIL_ERROR =
  'Please verify your email address with OTP before continuing.';

/**
 * Dev-only bypass: set REACT_APP_DEV_BYPASS=true in .env.local to:
 *   - skip the /login navigation guard
 *   - skip form validation (OTP, pin, face_index)
 *   - inject dummy face_index and pin so /register/payment works directly
 * Hard-gated by NODE_ENV so it CANNOT be active in production.
 */
const IS_DEV_BYPASS_ENABLED =
  process.env.REACT_APP_DEV_BYPASS === 'true' &&
  process.env.NODE_ENV !== 'production';

export interface RegistrationFlowHandlers {
  // Form state
  phoneNumber: string | undefined;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  countryPhoneCode: string | undefined;
  setCountryPhoneCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  name: string | undefined;
  setName: React.Dispatch<React.SetStateAction<string | undefined>>;
  email: string | undefined;
  setEmail: React.Dispatch<React.SetStateAction<string | undefined>>;
  nationality: string;
  setNationality: React.Dispatch<React.SetStateAction<string>>;
  phoneVerificationToken: string | undefined;
  setPhoneVerificationToken: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  emailVerificationToken: string | undefined;
  setEmailVerificationToken: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  nameTouched: boolean;
  setNameTouched: React.Dispatch<React.SetStateAction<boolean>>;
  emailTouched: boolean;
  setEmailTouched: React.Dispatch<React.SetStateAction<boolean>>;
  // Derived validation
  nameError: string | null;
  emailError: string | null;
  // Prefill (set once on mount from sessionStorage)
  initialPhoneNumber: string;
  initialCountryCode: string;
  initialPhoneVerificationToken: string | undefined;
  // UI state
  checked: boolean;
  setChecked: React.Dispatch<React.SetStateAction<boolean>>;
  isDryRunChecked: boolean;
  setIsDryRunChecked: React.Dispatch<React.SetStateAction<boolean>>;
  errorMessage: string | undefined;
  setErrorMessage: React.Dispatch<React.SetStateAction<string | undefined>>;
  isSubmitting: boolean;
  showImportantNotes: boolean;
  setShowImportantNotes: React.Dispatch<React.SetStateAction<boolean>>;
  // Step navigation
  paymentStep: PaymentMethodStep;
  selectedBrand: PaymentMethodBrand | null;
  // Navigation handlers
  goToPaymentTypeSelection: () => void;
  goToCardBrandSelection: () => void;
  goToEWalletBrandSelection: () => void;
  goToAccountDetails: () => void;
  confirmBrandSelection: (brand: PaymentMethodBrand) => void;
  clearBrandSelection: () => void;
  // Submit actions
  handleSubmit: () => Promise<void>;
  doRegistration: () => Promise<void>;
  // Feature flags
  isRegisterDryRunFeatureEnabled: boolean;
  isEWalletEnabled: boolean;
  isDevBypassEnabled: boolean;
}

export function useRegistrationFlow(): RegistrationFlowHandlers {
  const navigate = useNavigate();

  // Form state
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [countryPhoneCode, setCountryPhoneCode] = useState<
    string | undefined
  >();
  const [name, setName] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [nationality, setNationality] = useState('MY');
  const [phoneVerificationToken, setPhoneVerificationToken] = useState<
    string | undefined
  >();
  const [emailVerificationToken, setEmailVerificationToken] = useState<
    string | undefined
  >();
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  // Prefill
  const [initialPhoneNumber, setInitialPhoneNumber] = useState('');
  const [initialCountryCode, setInitialCountryCode] = useState('');
  const [initialPhoneVerificationToken, setInitialPhoneVerificationToken] =
    useState<string | undefined>();

  // UI state
  const [checked, setChecked] = useState(false);
  const [isDryRunChecked, setIsDryRunChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImportantNotes, setShowImportantNotes] = useState(false);

  // Step / payment navigation
  const [paymentStep, setPaymentStep] =
    useState<PaymentMethodStep>('account-details');
  const [selectedBrand, setSelectedBrand] = useState<PaymentMethodBrand | null>(
    null
  );

  // Derived validation (memoized to avoid recalculation in consuming components)
  const nameError = useMemo((): string | null => {
    if (!name || name.trim().length === 0) return 'Name is required';
    return null;
  }, [name]);

  const emailError = useMemo((): string | null => {
    if (!email || email.trim().length === 0) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  }, [email]);

  // Prefill from sessionStorage on mount + navigation guard
  useEffect(() => {
    const externalPhone = sessionStorage.getItem(
      'external_prefill_phone_number'
    );
    const externalCountryCode = sessionStorage.getItem(
      'external_prefill_country_code'
    );
    const externalName = sessionStorage.getItem('external_prefill_full_name');
    const externalEmail = sessionStorage.getItem('external_prefill_email');
    const externalPhoneToken = sessionStorage.getItem(
      'external_prefill_phone_verification_token'
    );

    if (externalPhone) setPhoneNumber(externalPhone);

    if (externalCountryCode) {
      const clean = externalCountryCode.replace('+', '');
      setCountryPhoneCode(clean);
      setInitialCountryCode(clean);
      setInitialPhoneNumber(`${clean}${externalPhone || ''}`);
    }

    if (externalPhoneToken)
      setInitialPhoneVerificationToken(externalPhoneToken);
    if (externalName) setName(externalName);
    if (externalEmail) setEmail(externalEmail);

    const registrationStarted =
      sessionStorage.getItem('registration_started') === 'true';
    const step = sessionStorage.getItem('step');

    if (
      !IS_DEV_BYPASS_ENABLED &&
      (!(step === '1' || step === '2') || !registrationStarted)
    ) {
      navigate('/login');
    }
  }, [navigate]);

  // Best-effort country prefill from backend geolocation.
  useEffect(() => {
    const externalCountryCode = sessionStorage.getItem(
      'external_prefill_country_code'
    );
    if (
      externalCountryCode ||
      initialCountryCode ||
      countryPhoneCode ||
      phoneNumber
    ) {
      return;
    }

    let isMounted = true;

    const prefillCountry = async () => {
      try {
        const response = await ayeApi.getRegistrationCountryPrefill({});
        if (!response.success || !isMounted) return;

        const dialCode = (response.data?.dialCode || '+60').replace('+', '');
        const countryCode = (response.data?.countryCode || 'MY').toUpperCase();

        if (!dialCode) return;

        setCountryPhoneCode(dialCode);
        setInitialCountryCode(dialCode);
        setInitialPhoneNumber(dialCode);

        // Only replace default nationality fallback.
        setNationality((prev) => (prev === 'MY' ? countryCode : prev));
      } catch {
        // Non-blocking: keep local defaults when country prefill is unavailable.
      }
    };

    void prefillCountry();

    return () => {
      isMounted = false;
    };
  }, [countryPhoneCode, initialCountryCode, phoneNumber]);

  // Auto-clear field/verification errors when conditions become valid
  useEffect(() => {
    const face_index = sessionStorage.getItem('face_index');
    const isRequiredFieldsValid =
      !!phoneNumber &&
      !!face_index &&
      nameError === null &&
      emailError === null;

    if (
      errorMessage?.startsWith(REQUIRED_FIELDS_ERROR_PREFIX) &&
      isRequiredFieldsValid
    ) {
      setErrorMessage(undefined);
      return;
    }

    const isPhoneVerified = !!phoneVerificationToken;
    const isEmailVerified = !!emailVerificationToken;

    if (
      errorMessage === VERIFY_BOTH_ERROR &&
      isRequiredFieldsValid &&
      isPhoneVerified &&
      isEmailVerified
    ) {
      setErrorMessage(undefined);
      return;
    }

    if (
      errorMessage === VERIFY_PHONE_ERROR &&
      isRequiredFieldsValid &&
      isPhoneVerified
    ) {
      setErrorMessage(undefined);
      return;
    }

    if (
      errorMessage === VERIFY_EMAIL_ERROR &&
      isRequiredFieldsValid &&
      isEmailVerified
    ) {
      setErrorMessage(undefined);
    }
  }, [
    phoneNumber,
    nameError,
    emailError,
    phoneVerificationToken,
    emailVerificationToken,
    errorMessage
  ]);

  // Make the device/browser Back button step back through the payment sub-flow
  // instead of leaving /register entirely. The payment sub-steps are React state,
  // not routes, so without this a hardware Back pops the previous page (the landing
  // register screen) and the user loses their whole registration. We guard the
  // entire payment sub-flow with a single history entry: Back pops it and returns
  // to account details. Depend on the boolean (not paymentStep) so moving between
  // payment sub-steps doesn't push extra entries.
  const inPaymentFlow = paymentStep !== 'account-details';
  const leftFlowViaBackRef = useRef(false);
  useEffect(() => {
    if (!inPaymentFlow) return;

    leftFlowViaBackRef.current = false;
    window.history.pushState({ registerPaymentFlow: true }, '');

    const handlePopState = () => {
      leftFlowViaBackRef.current = true;
      setPaymentStep('account-details');
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // If we returned to account details via an in-app control (Back / Skip /
      // brand-confirm) rather than a Back press, our sentinel is still on the
      // stack — pop it so history depth stays consistent.
      if (!leftFlowViaBackRef.current) {
        window.history.back();
      }
    };
  }, [inPaymentFlow]);

  // --- Validation helper (does not call API) ---
  const validateForm = useCallback((): boolean => {
    if (IS_DEV_BYPASS_ENABLED) return true;
    const face_index = sessionStorage.getItem('face_index');

    setNameTouched(true);
    setEmailTouched(true);

    const missing: string[] = [];
    if (!phoneNumber) missing.push('Phone number');
    if (!face_index) missing.push('Face verification');
    if (nameError !== null) missing.push('Name');
    if (emailError !== null) missing.push('Email');

    if (missing.length > 0) {
      setErrorMessage(`${REQUIRED_FIELDS_ERROR_PREFIX} ${missing.join(', ')}.`);
      return false;
    }

    if (!phoneVerificationToken && !emailVerificationToken) {
      setErrorMessage(VERIFY_BOTH_ERROR);
      return false;
    }
    if (!phoneVerificationToken) {
      setErrorMessage(VERIFY_PHONE_ERROR);
      return false;
    }
    if (!emailVerificationToken) {
      setErrorMessage(VERIFY_EMAIL_ERROR);
      return false;
    }
    if (!checked) {
      setErrorMessage('Please accept terms and conditions.');
      return false;
    }

    setErrorMessage(undefined);
    return true;
  }, [
    phoneNumber,
    nameError,
    emailError,
    phoneVerificationToken,
    emailVerificationToken,
    checked
  ]);

  // --- Core API call ---
  const doRegistration = useCallback(async () => {
    const face_index =
      sessionStorage.getItem('face_index') ??
      (IS_DEV_BYPASS_ENABLED ? 'dev-face-index' : null);

    setIsSubmitting(true);
    try {
      const response: GeneratePayUrlResponse = await ayeApi.generatePayUrl({
        body: {
          phoneNumber: `${phoneNumber}`,
          countryCode: countryPhoneCode || '',
          faceIndex: face_index || '',
          screen: 'register',
          nationality,
          name: name || '',
          email: email || '',
          ref: localStorage.getItem(REFERRAL_CODE_KEY) || undefined,
          ong_key: sessionStorage.getItem('ong_key') || undefined,
          checkout_session_id:
            sessionStorage.getItem('checkout_session_id') || undefined,
          external_session_id:
            sessionStorage.getItem('external_session_id') || undefined,
          dry_run: isDryRunChecked ? 'true' : undefined,
          phoneVerificationToken,
          emailVerificationToken,
          payment_method: selectedBrand ?? undefined
        }
      });

      sessionStorage.setItem('step', '3');
      sessionStorage.setItem('phoneComplete', 'true');

      if (response.success) {
        window.open(response.data.payUrl, '_self');
      } else {
        setErrorMessage(response.error.message);
      }
    } catch {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      setShowImportantNotes(false);
    }
  }, [
    phoneNumber,
    countryPhoneCode,
    nationality,
    name,
    email,
    isDryRunChecked,
    phoneVerificationToken,
    emailVerificationToken,
    selectedBrand
  ]);

  // --- Submit handler (validates → maybe shows dialog → calls doRegistration) ---
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    if (selectedBrand) {
      try {
        const response = await ayeApi.resolveGateway({
          queryParams: { payment_method: selectedBrand }
        });
        const requiresNotes =
          GATEWAY_REQUIRES_IMPORTANT_NOTES[
            (response as { data?: { gateway?: string } }).data?.gateway ?? ''
          ] ??
          PAYMENT_METHOD_CONFIG[selectedBrand]?.requiresImportantNotes ??
          false;
        if (requiresNotes) {
          setShowImportantNotes(true);
          return;
        }
      } catch {
        // Fallback to static config on API error
        if (PAYMENT_METHOD_CONFIG[selectedBrand]?.requiresImportantNotes) {
          setShowImportantNotes(true);
          return;
        }
      }
    }

    await doRegistration();
  }, [validateForm, selectedBrand, doRegistration]);

  // --- Step navigation ---
  const goToPaymentTypeSelection = useCallback(() => {
    setErrorMessage(undefined);
    setPaymentStep('select-payment-type');
  }, []);

  const goToCardBrandSelection = useCallback(() => {
    setPaymentStep('select-card-brand');
  }, []);

  const goToEWalletBrandSelection = useCallback(() => {
    setPaymentStep('select-ewallet-brand');
  }, []);

  const goToAccountDetails = useCallback(() => {
    setPaymentStep('account-details');
  }, []);

  const confirmBrandSelection = useCallback((brand: PaymentMethodBrand) => {
    setSelectedBrand(brand);
    setPaymentStep('account-details');
  }, []);

  const clearBrandSelection = useCallback(() => {
    setSelectedBrand(null);
  }, []);

  return {
    // Form state
    phoneNumber,
    setPhoneNumber,
    countryPhoneCode,
    setCountryPhoneCode,
    name,
    setName,
    email,
    setEmail,
    nationality,
    setNationality,
    phoneVerificationToken,
    setPhoneVerificationToken,
    emailVerificationToken,
    setEmailVerificationToken,
    nameTouched,
    setNameTouched,
    emailTouched,
    setEmailTouched,
    // Validation
    nameError,
    emailError,
    // Prefill
    initialPhoneNumber,
    initialCountryCode,
    initialPhoneVerificationToken,
    // UI
    checked,
    setChecked,
    isDryRunChecked,
    setIsDryRunChecked,
    errorMessage,
    setErrorMessage,
    isSubmitting,
    showImportantNotes,
    setShowImportantNotes,
    // Step navigation
    paymentStep,
    selectedBrand,
    goToPaymentTypeSelection,
    goToCardBrandSelection,
    goToEWalletBrandSelection,
    goToAccountDetails,
    confirmBrandSelection,
    clearBrandSelection,
    // Actions
    handleSubmit,
    doRegistration,
    // Feature flags
    isRegisterDryRunFeatureEnabled: IS_DRY_RUN_ENABLED,
    isEWalletEnabled: IS_EWALLET_ENABLED,
    isDevBypassEnabled: IS_DEV_BYPASS_ENABLED
  };
}
